import { LegendToggle, Save } from "@mui/icons-material";
import { Box, Fab,  FormControl,  IconButton,  ListSubheader,  MenuItem,  Stack,  Tooltip,  Typography, useTheme } from "@mui/material";
import { CellValueChangedEvent} from "ag-grid-community";
import React, { forwardRef, Ref, useEffect, useImperativeHandle, useRef } from "react";
import { useMemo, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { RowDef } from "../../components/ag-grid-extensions/models/RowDef";
import Form from "../../components/forms/form/Form";
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import { useOfflineDetectContext } from "../../contexts/offline-detect-context/UseOfflineDetectContext";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { useSaveCustomFormInstance } from "../../queries/custom-form-instances/UseSaveCustomFormInstance";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { IFormLegend, IFormTemplate } from "../../services/custom-forms-data-service/TemplateModels";
import { FormFeatureEnum, FormValueDataType, IFormInstanceData, IFormInstanceValue, } from "../../views/object-form-view/Models";
import FormCheckbox from "../forms/form-checkbox/FormCheckbox";
import FormProperty from "../forms/form-property/FormProperty";
import SideSheet from "../side-sheet/side-sheet/SideSheet";
import CustomFormRunnerUtils, { CustomFormType, FormulasType, FORM_CLOSED_SIGNATURE_CONTROL, FORM_CREATED_SIGNATURE_CONTROL } from "./CustomFormRunnerUtils";
import CustomFormRenderControls from "./renderers/CustomFormRenderControls";
import CustomFormLegend from "./renderers/CustomFormLegend";
import { ContentContainer } from "../content-container/ContentContainer";
import { ContentSection } from "../content-section/ContentSection";
import {isMobile, isTablet} from 'react-device-detect';
import { IAttachment } from "../../common/models/IAttachment";
import { FormAttachmentActionCardEnum } from "../forms/form-attachment-card/FormAttachmentCard";
import { ApplicationMimeTypesEnum } from "../../common/enums/ApplicationMimeTypesEnum";
import { FilestoreTypeEnum } from "../../common/enums/FilestoreTypeEnum";
import { getAttachmentUrl } from "../../common/utilities/AttachmentHelpers";
import FileDownloader, { FileDownloaderType } from "../file-downloader/FileDownloader";
import { bindCameraDialog, CameraDialog, useCameraDialogState } from "../camera-dialog/CameraDialog";
import FileViewerDialog, { bindFileViewerDialog, useFileViewerDialogState } from "../file-viewer-dialog/FileViewerDialog";
import _ from "lodash";
import FormSelect from "../forms/form-select/FormSelect";
import { useObjectViewState } from "../../queries/object-view-state/UseObjectViewState";
import { useCustomFormTemplate } from "../../queries/custom-form-templates/UseCustomFormTemplates";
import CustomFormAttachements from "./renderers/CustomFormAttachments";
import { getTimeframeTimeDisplayText } from "../../common/utilities/TimeframeUtilities";
import { TimeframeTypeEnum } from "../../common/enums/TimeframeTypeEnum";
import { IeLynxObject } from "../../common/models/eLynxObject";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";

export type CustomFormRunnerModeType = 'add' | 'edit' | 'view';

export type CustomFormRunnerProps = {
    mode?: CustomFormRunnerModeType;
    model: IFormTemplate;
    instanceData?: IFormInstanceData,
    availableObjects?: IeLynxObject[],
    //onSubmit?: (sessionToken: string) => void;
    onSubmit?: () => void;
    startTime?: Date,
    endTime?: Date
};

export type CustomFormRunnerType = {
    hasChanges: () => boolean;
};

function CustomFormRunner({
    mode,
    model,
    instanceData,
    availableObjects,
    startTime,
    endTime,
    onSubmit
}: CustomFormRunnerProps, ref: Ref<unknown> | undefined) {

    const isPhone = isMobile && !isTablet;

    const { t } = useTranslation();
    const theme = useTheme();

    const [gridChanges, setGridChanges] = useState<RowDef[]>([]);

    const routeContext = useRouteContext();
    const [userState] = useUserContext();
    const [offlineState] = useOfflineDetectContext();
    const {acknowledge, confirm } = useConfirmation();
    const [showLegend, setShowLegend] = useState(false);
    const cameraDialog = useCameraDialogState();
    const fileViewerDialog = useFileViewerDialogState();
    const fileDownloaderRef = useRef<FileDownloaderType>(null);

    const [formValue] = useState<CustomFormType>(CustomFormRunnerUtils.mapToForm(model, instanceData, routeContext.contextObject?.objectType));
    const [formulas] = useState<FormulasType>(CustomFormRunnerUtils.mapFormulas(model));

    const { data: objectViewState } = useObjectViewState({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number,
        contextObject: routeContext.contextObject,
        offline: offlineState.offline
    });


    const { mutate: saveFormInstance } = useQueryNotifications(useSaveCustomFormInstance)({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number,
        objectID: routeContext.contextObject?.id as number,
        objectType: routeContext.contextObject?.objectType as eLynxObjectTypeEnum,
        offline: offlineState.offline,
        startTime: startTime,
        endTime: endTime,
        onSuccess: () => {
            acknowledge(t('forms.formDataSaved', {name: model.name}))
        }
    });

    const { refetch: reloadTemplates } = useQueryNotifications(useCustomFormTemplate)({
        customerID: userState.customer?.id as number,
        routeContext: routeContext,
        offlineDataLoaded: offlineState.offlineDataLoaded,
        offline: offlineState.offline
    });  



    const form = useForm<CustomFormType>({
        mode: "onChange",
        defaultValues: useMemo(() => {
            //console.log(formValue)
            return {...formValue
        };
        }, [formValue])
    });

    const {
        handleSubmit,
        formState: {
            //errors,
            isDirty,
            isValid,
            //dirtyFields
        },
        getValues,
        setValue,
        watch,
        control,
        //reset
        //trigger,
        setError
    } = form;

    const {
        fields: attachments,
        append: appendAttachments,
        remove: removeAttachment
    } = useFieldArray<CustomFormType, 'attachments', 'fieldKey'>({

        control: control,
        name: "attachments",
        keyName: "fieldKey"
    });

    //let hasChanges = (isDirty && isValid) || gridChanges.length > 0;
    let hasChanges = isDirty || gridChanges.length > 0;

    // this effect handles controls that have formulas
    useEffect(() => {
        const subscription = watch((value, { name, type }) => {
            //console.log(value, name, type);

            if (name && formulas && formulas[name]) {
                formulas[name].forEach(formula => {
                    const result = formula.func(getValues);

                    if (Number.isFinite(result)) {
                        setValue(formula.output, result);
                    } else {
                        setValue(formula.output, "");
                    }
                });
            }
        });

        return () => subscription.unsubscribe();
    }, [
        watch,
        formulas,
        getValues,
        setValue
    ]);

    const devices = availableObjects?.filter(o => o.objectType === eLynxObjectTypeEnum.Device) ?? [];
    const tanks = availableObjects?.filter(o => o.objectType === eLynxObjectTypeEnum.Tank) ?? [];
    const wells = availableObjects?.filter(o => o.objectType === eLynxObjectTypeEnum.Well) ?? [];
    const tankBatteries = availableObjects?.filter(o => o.objectType === eLynxObjectTypeEnum.TankBattery) ?? [];

    const attachmentsInTemplate = CustomFormRunnerUtils.hasAttachmentsControl(model);

    function getHasChanges() {
        return hasChanges;
    }

    useImperativeHandle<unknown, CustomFormRunnerType>(
        ref,
        () => ({
            hasChanges: getHasChanges
        })
    );

    const onDataChanged = (event: CellValueChangedEvent) => {

        //let newValues = [...gridChanges];
        let newValues = [...gridChanges];
        let existing = newValues.find(o => o.id === event.data.id);
        if (existing) {
             const ndx = newValues.indexOf(existing);
             if (ndx !== -1) {
                newValues.splice(ndx, 1);
             }
        }

        const cloned = _.cloneDeep(event.data)
        newValues.push(cloned);

        setGridChanges(newValues);
    };

    const getFormInstanceToSave = (formValues: IFormInstanceValue<FormValueDataType | Array<IFormInstanceValue<FormValueDataType>[]>>[],
        attachments: IAttachment[],
        closed: boolean,
        objectkey: string | undefined) => {

            const user = userState.user?.displayText ?? '';
            const timestamp = new Date();

            let objectType: eLynxObjectTypeEnum | undefined;
            let objectID: number | undefined;

            if (objectkey) {
                const keyParts = objectkey.split(':');
                const [ot, oid] = keyParts;
                objectType = +ot as eLynxObjectTypeEnum;
                objectID = +oid;
            }

            const objectName = routeContext.contextObject?.objectType === eLynxObjectTypeEnum.Group ? 
                                    availableObjects?.find(o => o.id === objectID && o.objectType === objectType)?.displayText :
                                    objectViewState?.contextObject.objectName ?? "";
            
            return mode === 'add' ? {
                id: undefined,
                templateID: model.id,
                objectName: objectName,
                objectType: routeContext.contextObject?.objectType === eLynxObjectTypeEnum.Group ?
                                    objectType : 
                                    routeContext.contextObject?.objectType as eLynxObjectTypeEnum,
                objectID: routeContext.contextObject?.objectType === eLynxObjectTypeEnum.Group ?
                                    objectID :
                                    routeContext.contextObject?.id as number,
                formType: model.formType,
                formFeatures: model.formFeatures,
                openedTimestamp: timestamp,
                openedBy: user,
                closedBy: closed ? user : undefined,
                closedTimestamp: closed ? timestamp : undefined,
                history: [{
                    modifiedBy: user,
                    modifiedTimestamp: timestamp,
                    values: formValues
                }],
                attachments: attachments
            } as IFormInstanceData : {
                ...instanceData,
                closedBy: closed ? user : undefined,
                closedTimestamp: closed ? timestamp : undefined,
                history: [...(instanceData?.history ?? []), {
                    modifiedBy: user,
                    modifiedTimestamp: timestamp,
                    values: formValues
                }],
                attachments: attachments
            } as IFormInstanceData;
    }


    async function getSignatureConfirmation() {

        return (await confirm({
            dialogTitle: t("customForms.signature.title"),
            confirmationText: t("customForms.signature.text"),
            yesButtonText: t("customForms.signature.signAndSave"),
            noButtonText: t("customForms.signature.noGoBack"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true,
            showCloseButton: false
        })) === "yes";
    }

    const onSubmitFormData = async (event: React.MouseEvent<HTMLButtonElement>,
            values: CustomFormType) => {

        event.preventDefault();

        const attachments = values['attachments'] as IAttachment[];
        const objectkey = routeContext.contextObject?.objectType === eLynxObjectTypeEnum.Group && 
                    !instanceData ? 
                        values['objectkey'] as string : 
                        undefined;

        const closed = (values['closeform'] as boolean) || ((model.formFeatures & FormFeatureEnum.allowClose) === 0);

        let formValues = CustomFormRunnerUtils.getFormInstanceValues(model, instanceData, values, gridChanges);

        let instanceToSave = getFormInstanceToSave(formValues, attachments, closed, objectkey);

        if (mode === 'add' && (model.formFeatures & FormFeatureEnum.requireSignature) > 0) {

            const confirmed = await getSignatureConfirmation();

            if (!confirmed) {
                return;
            }

            let signatureControl = CustomFormRunnerUtils.findFormControl(model, FORM_CREATED_SIGNATURE_CONTROL)

            if (signatureControl) {
                setValue(FORM_CREATED_SIGNATURE_CONTROL, `${instanceToSave.openedBy} ${getTimeframeTimeDisplayText(TimeframeTypeEnum.None, instanceToSave.openedTimestamp)}`);
            }

            if (closed) {
                signatureControl = CustomFormRunnerUtils.findFormControl(model, FORM_CLOSED_SIGNATURE_CONTROL)

                if (signatureControl) {
                    setValue(FORM_CLOSED_SIGNATURE_CONTROL, `${instanceToSave.closedBy} ${getTimeframeTimeDisplayText(TimeframeTypeEnum.None, instanceToSave.closedTimestamp as Date)}`);
                }
            }

            // get the updated values for the signatures
            formValues = CustomFormRunnerUtils.getFormInstanceValues(model, instanceData, getValues(), gridChanges);
            instanceToSave = getFormInstanceToSave(formValues, attachments, closed, objectkey);

        } else if (mode === 'edit' && 
                (model.formFeatures & FormFeatureEnum.requireSignature) > 0 && 
                (model.formFeatures & FormFeatureEnum.allowClose) > 0 && 
                closed)
        {
            const confirmed = await getSignatureConfirmation();

            if (!confirmed) {
                return;
            }

            if (closed) {
                const signatureControl = CustomFormRunnerUtils.findFormControl(model, FORM_CLOSED_SIGNATURE_CONTROL)

                if (signatureControl) {
                    setValue(FORM_CLOSED_SIGNATURE_CONTROL, `${instanceToSave.closedBy} ${getTimeframeTimeDisplayText(TimeframeTypeEnum.None, instanceToSave.closedTimestamp as Date)}`);

                    // get the updated values for the signatures
                    formValues = CustomFormRunnerUtils.getFormInstanceValues(model, instanceData, getValues(), gridChanges);
                    instanceToSave = getFormInstanceToSave(formValues, attachments, closed, objectkey);
                }                
            }
        }

        saveFormInstance(instanceToSave,
        {
            onSuccess: (data) => {
                reloadTemplates();
                onSubmit && onSubmit();
            }
        });

        setGridChanges([]);
    }

    async function onOpenCamera() {
        const result = await cameraDialog.open();

        if (result.reason === "ok") {
            const regex = /-([0-9]*?)\./;
            const maxNumber = attachments
                .filter(i => i.fileName?.startsWith("Image"))
                .reduce<number>((prev, cur) => {

                    const fileName = cur.fileName ?? "";
                    const matched = regex.exec(fileName);

                    if (matched && matched.length > 0) {
                        const num: number = parseInt(matched[1], 10);
                        return num > prev ? num : prev;
                    }

                    return prev;
                }, 0);

            const fileName = `Image-${maxNumber < 9 ? 0 : ""}${maxNumber + 1}`;

            const attachment: IAttachment = {
                id: -1,
                fileName: `${fileName}.jpg`,
                description: fileName,
                deleted: false,
                filestoreType: FilestoreTypeEnum.Images,
                lastModified: undefined,
                mimeType: ApplicationMimeTypesEnum.JPEG,
                base64Data: result.values?.base64imageData,
                url: undefined,
                thumbnailUrl: undefined
            } as IAttachment;

            appendAttachments(attachment);
        }
    }

    async function onAttachmentAction(action: FormAttachmentActionCardEnum,
        index: number,
        attachment: IAttachment) {
        if (action === FormAttachmentActionCardEnum.View) {

            await fileViewerDialog.open({
                title: attachment.description,
                url: getUrl(attachment.base64Data, attachment.url),
                mimeType: attachment.mimeType
            });
        } else if (action === FormAttachmentActionCardEnum.Delete) {

            removeAttachment(index);
        } else if (action === FormAttachmentActionCardEnum.Download) {

            fileDownloaderRef.current?.download(getAttachmentUrl(attachment.base64Data, attachment.url), attachment.fileName);
        }
    }

    function getUrl(base64Data: string | undefined, url: string | undefined): string | undefined {
        return base64Data
            ? `data:image/jpeg;base64,${base64Data}`
            : url;
    }

    useEffect(() => {
        if (availableObjects) {
            setError('objectkey', { 
                message: t('customForms.objectRequired') as string}
            );
        }
    },[
        availableObjects,
        setError,
        t
    ]);

    const _isValidateObject = (value: string): string | undefined => {

        return value === '' ? t('customForms.objectRequired') as string : undefined;
    };

    //console.log(`getValues ${JSON.stringify(getValues())}`)
    //console.log(`form isDirty: ${isDirty} and valid: ${isValid} ${JSON.stringify(dirtyFields)} ${JSON.stringify(getValues())}`)
     return (
         <ContentContainer>
             <ContentSection>
                 <Form form={form}>

                 {model.template && model.template.legend &&
                 <Tooltip title={t('customForms.showLegend') as string}>
                   <IconButton size="large"
                       aria-label="options"
                       color={isPhone ? "inherit" : 'default'}
                       edge='end'
                       sx={{marginLeft: 'auto'}}
                       onClick={(e) => {
                           setShowLegend(true);
                           e.preventDefault();
                       }
                      }>
                     <LegendToggle />
                   </IconButton>
                 </Tooltip>
                 }

                     <Typography
                             variant="h6"
                             noWrap
                             component="div"
                             sx={{
                                 display: 'flex',
                                 flex: '0 0 auto',
                                 alignSelf: 'center'
                             }}>
                             {model.template.title}
                     </Typography>

                     {model.template.subTitle &&
                         <Typography
                             variant="subtitle1"
                             noWrap
                             component="div"
                             sx={{
                                 display: 'flex',
                                 flex: '0 0 auto',
                                 alignSelf: 'center',
                                 marginBottom: theme.spacing(1)
                             }}>
                             {model.template.subTitle}
                         </Typography>
                     }

                     <Typography
                             variant="body1"
                             noWrap
                             component="div"
                             sx={{
                                 display: 'flex',
                                 flex: '0 0 auto',
                                 alignSelf: 'flex-start',
                                 fontWeight: 600,
                                 marginBottom: theme.spacing(1)
                             }}>
                             {   `ID: ${mode === 'add' ?                           
                                 model.nextInstanceID :
                                 instanceData?.formID}`
                             }
                     </Typography>


                     <Box
                         sx={{
                             display: 'flex',
                             flexDirection: 'column',
                             overflow: 'auto',
                             flex: '1 1 auto',
                             marginTop: theme.spacing(1)
                         }}>


                     {availableObjects &&

                         <FormProperty>
                             <FormControl
                                 //aria-label={ariaLabel}
                                 variant="filled"
                                 sx={{
                                     marginTop: 0,
                                     width: '100%'
                                 }}>
                                 <FormSelect
                                     name="objectkey"
                                     id="objectselector"
                                     label={t('customForms.chooseObject')}
                                     rules={{
                                         required: true,
                                         validate: (value: any) => _isValidateObject(value)
                                     }}
                                     helperText={t('customForms.objectRequired')}
                                 >

                                     {devices.length > 0 && [

                                         <ListSubheader key='devices'>
                                             <OptionGroupHeading name={t('objectTypeNames.devices')} />
                                         </ListSubheader>,

                                         devices.map((o, index) => 
                                             <MenuItem key={`d${index}`} value={`${o.objectType}:${o.id}`}>
                                                 <Box component="span" sx={{ paddingLeft: 2 }}>
                                                     {o.displayText}
                                                 </Box>
                                             </MenuItem>

                                             )
                                         ]
                                     }

                                     {tanks.length > 0 && [

                                     <ListSubheader key='tanks'>
                                         <OptionGroupHeading name={t('objectTypeNames.tanks')} />
                                     </ListSubheader>,

                                     tanks.map((o, index) => 
                                         <MenuItem key={`t${index}`} value={`${o.objectType}:${o.id}`}>
                                             <Box component="span" sx={{ paddingLeft: 2 }}>
                                                 {o.displayText}
                                             </Box>
                                         </MenuItem>

                                         )
                                     ]
                                     }


                                     {tankBatteries.length > 0 && [

                                     <ListSubheader key='tankbattery'>
                                         <OptionGroupHeading name={t('objectTypeNames.tankBatteries')} />
                                     </ListSubheader>,

                                     tankBatteries.map((o, index) => 
                                         <MenuItem key={`tb${index}`} value={`${o.objectType}:${o.id}`}>
                                             <Box component="span" sx={{ paddingLeft: 2 }}>
                                                 {o.displayText}
                                             </Box>
                                         </MenuItem>

                                         )
                                     ]
                                     }


                                     {wells.length > 0 && [

                                     <ListSubheader key='wells'>
                                         <OptionGroupHeading name={t('objectTypeNames.wells')} />
                                     </ListSubheader>,

                                     wells.map((o, index) => 
                                         <MenuItem key={`w${index}`} value={`${o.objectType}:${o.id}`}>
                                             <Box component="span" sx={{ paddingLeft: 2 }}>
                                                 {o.displayText}
                                             </Box>
                                         </MenuItem>

                                         )
                                     ]
                                     }

                                 </FormSelect>
                             </FormControl>
                         </FormProperty>
                     }


                         <CustomFormRenderControls
                                 mode={mode}
                                 controls={model.template.controls}
                                 instanceData={instanceData}
                                 onDataChanged={onDataChanged} 
                                 attachments={attachments} 
                                 onAttachmentAction={onAttachmentAction} 
                                 onOpenCamera={onOpenCamera}
                                 />



                         {!attachmentsInTemplate && (model.formFeatures & FormFeatureEnum.allowAttachments) > 0 &&

                                 <CustomFormAttachements mode={mode} 
                                     attachments={attachments} 
                                     onAttachmentAction={onAttachmentAction} 
                                     onOpenCamera={onOpenCamera} />

                         // <FormPropertySection label={
                         //     <Stack spacing={0} direction="row" alignItems="center">
                         //         {mode === 'view' && attachments.length === 0 ? null :
                         //             <Typography component="span" variant="subtitle1" fontWeight={600} >
                         //                 {t('customForms.attachments')}
                         //             </Typography>
                         //         }

                         //         {mode !== 'view' &&
                         //             <IconButton color="inherit" onClick={
                         //                 () => onOpenCamera()
                         //             }>
                         //                 <AddAPhoto />
                         //             </IconButton>
                         //         }

                         //     </Stack>

                         //     }>

                         //     {attachments.map((field, index) => (
                         //         <FormProperty key={field.fieldKey}>
                         //             <FormAttachmentCard
                         //                 disabled={mode === 'view'}
                         //                 name={`attachments.${index}`}
                         //                 index={index}
                         //                 onAction={onAttachmentAction} />
                         //         </FormProperty>
                         //     ))}

                         // </FormPropertySection>
                         }

                         {mode !== 'view' &&
                             (model.formFeatures & FormFeatureEnum.allowClose) > 0 &&
                             instanceData?.closedTimestamp === undefined &&

                             <FormProperty
                                 sx={{
                                     display: 'flex',
                                     flex: '0 0 auto!important',
                                 }}>

                                 <FormCheckbox
                                     name="closeform"
                                     aria-label='close form'
                                     label={t('forms.close')}
                                 />
                             </FormProperty>
                         }

                     </Box>

                     {mode !== 'view' &&
                         <Fab
                             color="secondary"
                             id="submit"
                             type="submit"
                             name="update"
                             disabled={(!isDirty || !isValid) && gridChanges.length === 0}
                             aria-label="save form data"
                             sx={{
                                 position: "absolute",
                                 bottom: theme.spacing(2),
                                 right: theme.spacing(2)
                             }}
                             onClick={(e) => handleSubmit((data) => onSubmitFormData(e, data))()}>
                             <Save />
                         </Fab>
                     }
                 </Form>
             </ContentSection>
             {model.template && model.template.legend &&
                 <SideSheet open={showLegend}
                     title='Legend'
                     onClose={() => setShowLegend(false)}>


                     <Stack
                         direction='column'
                         sx={{
                             alignItems: 'baseline',
                             flex: '1 1 auto',
                             justifyContent: 'flex-end'
                         }}>

                     {model.template.legend?.map((o, index) =>
                         <CustomFormLegend legend={o as IFormLegend} key={index} />
                     )}

                     </Stack>


             </SideSheet>
             }
             <FileDownloader ref={fileDownloaderRef} />
             <CameraDialog {...bindCameraDialog(cameraDialog)} />
             <FileViewerDialog {...bindFileViewerDialog(fileViewerDialog)} />
         </ContentContainer>
     );
}

export default forwardRef(CustomFormRunner);

interface OptionGroupHeadingProps {
    name?: string | null;
}

function OptionGroupHeading({ name }: OptionGroupHeadingProps) {
    return (
        <Box component="span" sx={{
            fontSize: "1rem",
            textTransform: "uppercase",
            display: "block",
        }}>
            {name}
        </Box>
    );
}
